import { useEffect, useRef } from "react";
import { PANELIST_CONFIG } from "../avatar/AvatarProvider";

/*
 * OralBoardPanel — the immersive 3-person oral board scene.
 *
 * Driven purely by high-level props from the interview page:
 *   - activeName: name of the panelist currently speaking (or null)
 *   - speaking:   the active panelist is delivering a question (audio playing)
 *   - listening:  the candidate is answering (panel is attentive)
 *   - provider:   AvatarProvider instance (capabilities decide portrait vs video)
 *
 * If provider.capabilities().realtimeVideo is true, each seat attaches the
 * provider's live MediaStream to a <video>. Otherwise it shows the animated
 * photographic portraits (current, no paid API). Same layout either way, so a
 * realtime provider can be swapped in without changing this scene.
 */

function Seat({ panelist, isActive, speaking, listening, provider, index }) {
  const videoRef = useRef(null);
  const realtime = provider?.capabilities?.().realtimeVideo;

  useEffect(() => {
    if (realtime && videoRef.current) {
      const stream = provider.getStream(panelist.id);
      if (stream) videoRef.current.srcObject = stream;
    }
  }, [realtime, provider, panelist.id]);

  const speakingNow = isActive && speaking;
  const dim = !isActive && speaking; // soften non-speakers while someone talks

  return (
    <div
      className={`ob-seat ${speakingNow ? "ob-speaking" : ""} ${dim ? "ob-dim" : ""}`}
      data-testid={`panel-seat-${panelist.id}`}
      style={{ animationDelay: `${index * 0.9}s` }}
    >
      <div className="ob-frame" style={{ "--seat-accent": panelist.color }}>
        {realtime ? (
          <video ref={videoRef} autoPlay playsInline muted={!isActive} className="ob-media" />
        ) : (
          <div
            className={`ob-portrait ${speakingNow ? "ob-portrait-speaking" : "ob-portrait-idle"}`}
            style={{ backgroundImage: `url(${panelist.image})` }}
          />
        )}

        <div className="ob-grain" />

        {speakingNow && (
          <div className="ob-eq" data-testid={`panel-speaking-${panelist.id}`} aria-hidden>
            {[0, 1, 2, 3, 4].map((b) => (
              <span key={b} style={{ animationDelay: `${b * 0.12}s` }} />
            ))}
          </div>
        )}
        {isActive && listening && (
          <div className="ob-listening" data-testid={`panel-listening-${panelist.id}`}>Listening</div>
        )}
      </div>

      <div className="ob-plate">
        <span className="ob-name">{panelist.name}</span>
        <span className="ob-role">{panelist.role}</span>
      </div>
    </div>
  );
}

export function OralBoardPanel({ panelists = PANELIST_CONFIG, activeName, speaking, listening, provider, caption }) {
  const activeId = panelists.find((p) => p.name === activeName)?.id || null;

  return (
    <div className="ob-stage" data-testid="oral-board-panel">
      <div className="ob-room" style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ""}/panel/room_bg.jpg)` }} />
      <div className="ob-seats">
        {panelists.map((p, i) => (
          <Seat
            key={p.id}
            panelist={p}
            index={i}
            isActive={p.id === activeId}
            speaking={speaking}
            listening={listening}
            provider={provider}
          />
        ))}
      </div>
      <div className="ob-table" />
      <div className="ob-vignette" />
      {caption && <div className="ob-caption" data-testid="oral-board-caption">{caption}</div>}
    </div>
  );
}
